import React, { useRef, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface LazyLoadProps {
  children: React.ReactNode;
  className?: string;
  /** Fallback shown before the section enters view */
  fallback?: React.ReactNode;
  /** Root margin for intersection observer (default: 200px to preload early) */
  rootMargin?: string;
}

/**
 * Defers rendering of children until the placeholder enters the viewport.
 * Useful for heavy below-fold sections (maps, calculators, similar properties).
 */
export const LazySection: React.FC<LazyLoadProps> = ({
  children,
  className,
  fallback,
  rootMargin = '200px',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref} className={cn(className)}>
      {isVisible ? children : (fallback ?? <div className="h-32" />)}
    </div>
  );
};
